import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  useColorMode,
  SimpleGrid,
  Icon,
  Flex,
  Divider,
  Badge,
  Button,
  Link,
  Input,
  FormControl,
  FormLabel,
  FormHelperText,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Code,
  List,
  ListItem,
  ListIcon,
} from '@chakra-ui/react';
import { ExternalLinkIcon, CopyIcon } from '@chakra-ui/icons';
import { NavbarWithCallToAction } from '../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction';
import { FooterWithFourColumns } from '../../components/chakra/FooterWithFourColumns/FooterWithFourColumns';
import { getColor } from '../../brandConfig';
import { FaCoins, FaCheckCircle, FaArrowRight, FaGasPump, FaWallet, FaClock, FaTint } from 'react-icons/fa';
import { GiTwoCoins } from 'react-icons/gi';
import ListBuildingModal from '../../components/ListBuildingModal';

export const GasFees = () => {
  const { colorMode } = useColorMode();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [walletAddress, setWalletAddress] = useState('');
  const [requestSent, setRequestSent] = useState(false);

  const handleFaucetRequest = () => {
    if (walletAddress) {
      setRequestSent(true);
      setTimeout(() => setRequestSent(false), 5000);
    }
  };

  const gasFeeInfo = [
    {
      icon: FaCoins,
      title: 'B52 for Gas',
      description: 'Gas fees on Block52 are paid in B52 tokens, the native utility token of the network.',
      color: '#3b82f6'
    },
    {
      icon: GiTwoCoins,
      title: 'Minimal Costs',
      description: 'Gas fees are designed to be minimal—typically fractions of a cent per transaction.',
      color: '#64ffda'
    },
    {
      icon: FaGasPump,
      title: 'Variable Pricing',
      description: 'Gas prices adjust based on network congestion, ensuring fair allocation during busy periods.',
      color: '#3b82f6'
    },
    {
      icon: FaClock,
      title: 'Fast Finality',
      description: 'Transactions finalize in seconds, not minutes, thanks to Cosmos consensus.',
      color: '#64ffda'
    }
  ];

  const faucetInstructions = [
    'Copy your wallet address from the wallet page',
    'Paste your address into the faucet form below',
    'Click "Request Test Tokens"',
    'Receive 0.05 STAKE (test B52) to your wallet',
    'Wait 24 hours before requesting again'
  ];

  return (
    <>
      <NavbarWithCallToAction />
      <Box minH="100vh" bg={getColor('background.main', colorMode)} py={20}>
        <Container maxW="900px">
          <VStack spacing={8} align="start">
            {/* Header */}
            <VStack spacing={4} align="start" w="full">
              <Badge
                px={4}
                py={2}
                bgGradient="linear(to-r, #3b82f6, #64ffda)"
                color="white"
                borderRadius="full"
                fontSize="sm"
                fontWeight="bold"
              >
                LEARN
              </Badge>
              <Heading
                as="h1"
                size="2xl"
                color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}
              >
                Gas Fees
              </Heading>
              <Text fontSize="xl" color={getColor('text.secondary', colorMode)} lineHeight="tall">
                Gas fees are small transaction costs paid to process actions on the Block52 blockchain. Learn how
                gas works, how to get test tokens, and what to expect when the network launches.
              </Text>
            </VStack>

            <Divider />

            {/* What are Gas Fees */}
            <Box w="full">
              <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                What Are Gas Fees?
              </Heading>
              <Text color={getColor('text.secondary', colorMode)} lineHeight="tall" mb={4}>
                Gas fees are small amounts of cryptocurrency paid to validators who process transactions on the blockchain.
                When you place a bet, fold, or perform any action in a poker game, that action is recorded on-chain and
                requires computational resources to validate.
              </Text>
              <Text color={getColor('text.secondary', colorMode)} lineHeight="tall" mb={4}>
                On Block52, gas fees are paid in B52 tokens. The amount you pay depends on the complexity of the
                transaction and current network demand. Simple actions like betting or folding cost very little,
                while more complex operations like starting a tournament might cost slightly more.
              </Text>
              <Box
                p={6}
                bg={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'blue.50'}
                borderRadius="lg"
                borderLeft="4px solid"
                borderColor="#3b82f6"
              >
                <Text color={getColor('text.secondary', colorMode)} fontWeight="medium" fontStyle="italic">
                  "Gas fees on Block52 are designed to be minimal—typically costing fractions of a cent per transaction,
                  ensuring that playing poker remains affordable for everyone."
                </Text>
              </Box>
            </Box>

            {/* Gas Fee Features */}
            <Box w="full">
              <Heading as="h2" size="lg" mb={6} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                Gas Fee Features
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                {gasFeeInfo.map((item, index) => (
                  <Box
                    key={index}
                    p={6}
                    borderRadius="lg"
                    bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
                    border="1px solid"
                    borderColor={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'gray.200'}
                    transition="all 0.3s"
                    _hover={{
                      transform: 'translateY(-4px)',
                      borderColor: item.color,
                      boxShadow: `0 8px 16px ${item.color}30`,
                    }}
                  >
                    <Flex align="start" mb={3}>
                      <Flex
                        w="48px"
                        h="48px"
                        borderRadius="lg"
                        bg={`${item.color}20`}
                        align="center"
                        justify="center"
                        mr={4}
                        flexShrink={0}
                      >
                        <Icon as={item.icon} color={item.color} boxSize={6} />
                      </Flex>
                      <Heading size="sm" color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                        {item.title}
                      </Heading>
                    </Flex>
                    <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                      {item.description}
                    </Text>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>

            {/* Test Network: STAKE Token */}
            <Alert
              status="info"
              variant="left-accent"
              borderRadius="lg"
              bg={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'blue.50'}
              borderColor="#3b82f6"
            >
              <AlertIcon color="#3b82f6" />
              <Box>
                <AlertTitle color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                  Alpha Test Network
                </AlertTitle>
                <AlertDescription color={getColor('text.secondary', colorMode)}>
                  During the alpha test phase, we're using a token called <Code colorScheme="blue">STAKE</Code> instead
                  of B52. This is purely for testing purposes. By Q1 2026, we plan to transition to the real B52 token
                  for mainnet launch.
                </AlertDescription>
              </Box>
            </Alert>

            {/* Test Token Faucet */}
            <Box w="full">
              <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                Test Token Faucet
              </Heading>
              <Text color={getColor('text.secondary', colorMode)} lineHeight="tall" mb={4}>
                To interact with the Block52 alpha testnet, you need a small amount of STAKE tokens for gas fees.
                Use our faucet to request test tokens—completely free!
              </Text>

              <Box
                p={6}
                bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
                borderRadius="lg"
                border="2px solid"
                borderColor={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : '#3b82f6'}
              >
                <Flex align="center" mb={4}>
                  <Icon as={FaTint} color="#3b82f6" boxSize={8} mr={3} />
                  <Heading size="md" color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                    Block52 Test Faucet
                  </Heading>
                </Flex>

                <Text color={getColor('text.secondary', colorMode)} mb={4}>
                  Request <strong>0.05 STAKE</strong> tokens once every 24 hours
                </Text>

                <VStack spacing={4} align="start" w="full">
                  <FormControl>
                    <FormLabel color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                      Your Wallet Address
                    </FormLabel>
                    <Input
                      placeholder="block521a2b3c4d5e..."
                      value={walletAddress}
                      onChange={(e) => setWalletAddress(e.target.value)}
                      bg={colorMode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'white'}
                      borderColor={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'gray.300'}
                      _hover={{
                        borderColor: '#3b82f6',
                      }}
                      _focus={{
                        borderColor: '#3b82f6',
                        boxShadow: '0 0 0 1px #3b82f6',
                      }}
                    />
                    <FormHelperText color={getColor('text.secondary', colorMode)}>
                      <Flex align="center">
                        <Icon as={FaWallet} mr={2} />
                        <Text>
                          Find your wallet address at{' '}
                          <Link href="https://app.block52.xyz/wallet" isExternal color="#3b82f6">
                            app.block52.xyz/wallet <ExternalLinkIcon mx="2px" />
                          </Link>
                        </Text>
                      </Flex>
                    </FormHelperText>
                  </FormControl>

                  <Button
                    w="full"
                    bgGradient="linear(to-r, #3b82f6, #64ffda)"
                    color="white"
                    _hover={{
                      bgGradient: 'linear(to-r, #64ffda, #3b82f6)',
                    }}
                    onClick={handleFaucetRequest}
                    isDisabled={!walletAddress || requestSent}
                  >
                    {requestSent ? 'Request Sent!' : 'Request Test Tokens'}
                  </Button>

                  {requestSent && (
                    <Alert status="success" borderRadius="md">
                      <AlertIcon />
                      <Text fontSize="sm">
                        Your request has been submitted! Check your wallet in a few moments.
                      </Text>
                    </Alert>
                  )}
                </VStack>
              </Box>
            </Box>

            {/* How to Use the Faucet */}
            <Box w="full">
              <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                How to Use the Faucet
              </Heading>
              <List spacing={3}>
                {faucetInstructions.map((instruction, index) => (
                  <ListItem key={index}>
                    <Flex align="start">
                      <Flex
                        w="24px"
                        h="24px"
                        borderRadius="full"
                        bg="rgba(100, 255, 218, 0.2)"
                        align="center"
                        justify="center"
                        mr={3}
                        mt={0.5}
                        flexShrink={0}
                      >
                        <Text fontWeight="bold" fontSize="sm" color="#64ffda">{index + 1}</Text>
                      </Flex>
                      <Text color={getColor('text.secondary', colorMode)}>{instruction}</Text>
                    </Flex>
                  </ListItem>
                ))}
              </List>
            </Box>

            {/* Roadmap */}
            <Box
              w="full"
              p={6}
              bgGradient={colorMode === 'dark'
                ? 'linear(to-r, rgba(59, 130, 246, 0.1), rgba(100, 255, 218, 0.1))'
                : 'linear(to-r, blue.50, teal.50)'}
              borderRadius="xl"
              border="2px solid"
              borderColor={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : '#3b82f6'}
            >
              <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                Mainnet Roadmap
              </Heading>
              <VStack spacing={3} align="start">
                <Flex align="center">
                  <Icon as={FaCheckCircle} color="#64ffda" mr={3} fontSize="xl" />
                  <Text color={getColor('text.secondary', colorMode)}>
                    <strong>Now:</strong> Alpha testnet with STAKE tokens for testing
                  </Text>
                </Flex>
                <Flex align="center">
                  <Icon as={FaClock} color="#3b82f6" mr={3} fontSize="xl" />
                  <Text color={getColor('text.secondary', colorMode)}>
                    <strong>Q1 2026:</strong> Transition to real B52 token on mainnet
                  </Text>
                </Flex>
                <Flex align="center">
                  <Icon as={FaCoins} color="#64ffda" mr={3} fontSize="xl" />
                  <Text color={getColor('text.secondary', colorMode)}>
                    <strong>Future:</strong> Optimizations to reduce gas costs even further
                  </Text>
                </Flex>
              </VStack>
            </Box>

            {/* CTA */}
            <Box w="full" textAlign="center" py={6}>
              <Button
                size="lg"
                px={8}
                py={6}
                bgGradient="linear(to-r, #3b82f6, #64ffda)"
                color="white"
                _hover={{
                  bgGradient: 'linear(to-r, #64ffda, #3b82f6)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 20px rgba(59, 130, 246, 0.4)',
                }}
                rightIcon={<FaArrowRight />}
                borderRadius="full"
                fontWeight="bold"
                onClick={() => setIsModalOpen(true)}
              >
                Start Now
              </Button>
            </Box>
          </VStack>
        </Container>
      </Box>
      <FooterWithFourColumns />
      <ListBuildingModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
};

export default GasFees;
